import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../../../app/store";
import { cacheRpc } from "../../../../api";
import { ErrorResponseHandler } from "../../common/types";
import { setErrorSnackBarMessage } from "../../../systemSlice";
import {
  CacheRpcsSetCacheBody,
  MinioBucketCache,
  MinioCacheConfiguration,
  MinioTLSCache,
} from "../../../../api/cacheRpcApi";
import { errorToHandler } from "../../../../api/error";

export const fetchCacheDetailsAsync = createAsyncThunk(
  "cache/getCacheDetailsAsync",
  async (
    { siteId, resourceId }: { siteId: string; resourceId: string },
    { getState, rejectWithValue, dispatch },
  ) => {
    const state = getState() as RootState;

    const currentNamespace = state.tenants.currentNamespace;

    return cacheRpc.api
      .getCache(siteId, currentNamespace, resourceId)
      .then((res) => {
        return res.data;
      })
      .catch((err: ErrorResponseHandler) => {
        dispatch(setErrorSnackBarMessage(err));
        return rejectWithValue(err);
      });
  },
);

export const setCacheAsync = createAsyncThunk(
  "cache/addCacheAsync",
  async (
    { siteId, resourceId }: { siteId: string; resourceId: string },
    { getState, rejectWithValue, dispatch },
  ) => {
    const state = getState() as RootState;
    const currentNamespace = state.tenants.currentNamespace;
    const bucketMaxMem = state.tenantCache.formValues.cacheBucketMaxMem;

    const filteredBuckets =
      state.tenantCache.formValues.cacheBucketNames.filter(
        (element, index) =>
          element.trim() !== "" && bucketMaxMem[index]?.trim() !== "",
      );

    const buckets: MinioBucketCache[] = filteredBuckets.map((bucket, index) => {
      return {
        name: bucket,
        maxMemory: `${state.tenantCache.formValues.cacheBucketMaxMem[index]}GiB`,
      };
    });

    let TLSValues: MinioTLSCache | undefined = undefined;

    if (state.tenantCache.formValues.cacheTLSEnabled) {
      TLSValues = {
        publicKey: state.tenantCache.formValues.cacheTLSConfiguration.publicKey,
        privateKey:
          state.tenantCache.formValues.cacheTLSConfiguration.privateKey,
        caPath: state.tenantCache.formValues.cacheTLSConfiguration.caPath,
        clientAuth:
          state.tenantCache.formValues.cacheTLSConfiguration.clientAuth,
      };
    }

    const dt: MinioCacheConfiguration = {
      defaultMaxMemory: `${state.tenantCache.formValues.cacheDefaultMaxMemory}GiB`,
      buckets: buckets,
      tls: TLSValues,
    };

    const data: CacheRpcsSetCacheBody = {
      configData: dt,
    };

    return cacheRpc.api
      .setCache(siteId, currentNamespace, resourceId, data)
      .then((res) => {
        return res.data;
      })
      .catch((err: ErrorResponseHandler) => {
        dispatch(setErrorSnackBarMessage(err));
        return rejectWithValue(err);
      });
  },
);

export const deleteCacheInstance = createAsyncThunk(
  "cache/deleteCacheInstance",
  async (
    requestElements: { siteID: string; namespace: string; resourceID: string },
    { rejectWithValue, dispatch },
  ) => {
    return cacheRpc.api
      .deleteCache(
        requestElements.siteID,
        requestElements.namespace,
        requestElements.resourceID,
      )
      .then(() => {})
      .catch((res) => {
        dispatch(setErrorSnackBarMessage(errorToHandler(res.error)));
        return rejectWithValue(res);
      });
  },
);
